import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api/api';
import Seo from '../components/Seo';

var CDN = process.env.NODE_ENV==='development' ? 'https://cdn.j7lab.com/dev' : 'https://cdn.j7lab.com';

// Fonction utilitaire pour formater une liste d'éléments en français (virgules + "et")
const formatFrenchList = (items) => {
  if (!items || items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} et ${items[1]}`;
  
  const allButLast = items.slice(0, -1);
  const last = items[items.length - 1];
  return `${allButLast.join(', ')} et ${last}`;
};

// Fonction pour formater le nom à partir du slug
const formatName = (value) => {
  if (!value) return '';
  return value
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

// Seuils nutritionnels pour le positionnement des valeurs
const NUTRITION_THRESHOLDS = {
  calories: {
    bas: "< 100",
    moyen: "100–250",
    élevé: "> 250"
  },
  protein: {
    bas: "< 5",
    moyen: "5–15",
    élevé: "> 15"
  },
  carbs: {
    bas: "< 10",
    moyen: "10–30",
    élevé: "> 30"
  },
  sugar: {
    bas: "≤ 5",
    moyen: "5–12.5",
    élevé: "> 12.5"
  },
  fat: {
    bas: "≤ 3",
    moyen: "3–20",
    élevé: "> 20"
  },
  saturatedFat: {
    bas: "≤ 1.5",
    moyen: "1.5–5",
    élevé: "> 5"
  },
  fiber: {
    bas: "< 3",
    moyen: "3–6",
    élevé: "> 6"
  },
  salt: {
    bas: "≤ 0.3",
    moyen: "0.3–1.5",
    élevé: "> 1.5"
  },
  glycemicIndex: {
    bas: "< 55",
    moyen: "55–70",
    élevé: "> 70"
  }
};

const formatNutritionValue = (value, unit = 'g') => {
  if (typeof value === 'number') {
    return `${Math.round(value * 10) / 10} ${unit}`;
  }
  return '-';
};

// Fonction pour déterminer le niveau nutritionnel (bas, moyen, élevé)
const getNutritionLevel = (nutrientKey, value) => {
  if (value === undefined || value === null) return '-';
  
  const thresholds = NUTRITION_THRESHOLDS[nutrientKey];
  if (!thresholds) return '-';
  
  const numValue = parseFloat(value);
  
  switch (nutrientKey) {
    case 'calories':
      if (numValue < 100) return 'bas';
      if (numValue <= 250) return 'moyen';
      return 'élevé';
    
    case 'protein':
      if (numValue < 5) return 'bas';
      if (numValue <= 15) return 'moyen';
      return 'élevé';
    
    case 'carbs':
      if (numValue < 10) return 'bas';
      if (numValue <= 30) return 'moyen';
      return 'élevé';
    
    case 'sugar':
      if (numValue <= 5) return 'bas';
      if (numValue <= 12.5) return 'moyen';
      return 'élevé';
    
    case 'fat':
      if (numValue <= 3) return 'bas';
      if (numValue <= 20) return 'moyen';
      return 'élevé';
    
    case 'saturatedFat':
      if (numValue <= 1.5) return 'bas';
      if (numValue <= 5) return 'moyen';
      return 'élevé';
    
    case 'fiber':
      if (numValue < 3) return 'bas';
      if (numValue <= 6) return 'moyen';
      return 'élevé';
    
    case 'salt':
      if (numValue <= 0.3) return 'bas';
      if (numValue <= 1.5) return 'moyen';
      return 'élevé';
    
    case 'glycemicIndex':
      if (numValue < 55) return 'bas';
      if (numValue <= 70) return 'moyen';
      return 'élevé';
    
    default:
      return '-';
  }
};

// Fonction pour obtenir la couleur du niveau nutritionnel
const getNutritionLevelColor = (level) => {
  switch (level) {
    case 'bas': return '#22c55e'; // Vert
    case 'moyen': return '#f59e0b'; // Orange
    case 'élevé': return '#ef4444'; // Rouge
    default: return '#6c757d'; // Gris
  }
};

export default function ComboDetailPage() {
  const { Combo__slug, Combo___id } = useParams();
  const comboSlug=Combo__slug
  const comboId=Combo___id
  const [comboData, setComboData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPercentage, setShowPercentage] = useState(false);
  const [nutritionData, setNutritionData] = useState(null);
  const [nutritionLoading, setNutritionLoading] = useState(false);
  const [customProportions, setCustomProportions] = useState(null);

  // Debug: vérifier les paramètres de l'URL
  console.log('ComboDetailPage - Params:', { comboSlug, comboId });

  // Nom lisible du combo basé sur les ingrédients
  const comboName = useMemo(() => {
    if (comboData?.itemsList?.length) {
      return formatFrenchList(comboData.itemsList.map(item => item.name));
    }
    if (comboSlug) {
      // Extraire les noms d'ingrédients du slug
      const parts = comboSlug.split('_').filter(part => !part.includes('slugifie'));
      return formatFrenchList(parts.map(formatName));
    }
    return 'Combinaison d\'ingrédients';
  }, [comboData, comboSlug]);

  // Fonction pour initialiser les proportions personnalisées
  const initializeProportions = useCallback((itemsList, dosages) => {
    if (!itemsList?.length) return [];
    
    return itemsList.map((item, index) => {
      const originalDosage = dosages?.[index];
      const pourcentage = originalDosage?.pourcentage || Math.round(100 / itemsList.length);
      return {
        ingredientId: item._id,
        name: item.name,
        image: item.image,
        pourcentage: pourcentage,
        grammes: Math.round((pourcentage / 100) * 100) // 100g total par défaut
      };
    });
  }, []);

  // useEffect pour gérer le passage en mode grammes
  useEffect(() => {
    if (!showPercentage && comboData?.itemsList && comboData?.dosages) {
      // Quand on passe en mode grammes, réinitialiser avec 100g total selon les proportions optimales
      const proportionsFor100g = comboData.itemsList.map((item, index) => {
        const originalDosage = comboData.dosages[index];
        const pourcentage = originalDosage?.pourcentage || Math.round(100 / comboData.itemsList.length);
        return {
          ingredientId: item._id,
          name: item.name,
          image: item.image,
          pourcentage: pourcentage,
          grammes: Math.round((pourcentage / 100) * 100) // 100g total
        };
      });
      setCustomProportions(proportionsFor100g);
    }
  }, [showPercentage, comboData]);

  // Fonction pour ajuster une proportion
  const adjustProportion = (ingredientIndex, change, isPercentage = false) => {
    const currentProportions = customProportions || initializeProportions(comboData?.itemsList, comboData?.dosages);
    const newProportions = [...currentProportions];
    
    if (isPercentage) {
      // Ajustement en pourcentage - maintenir le total à 100%
      const newValue = Math.max(5, Math.min(90, newProportions[ingredientIndex].pourcentage + change));
      newProportions[ingredientIndex].pourcentage = newValue;
      
      // Recalculer pour que le total fasse 100%
      const otherIngredients = newProportions.filter((_, index) => index !== ingredientIndex);
      const remainingPercentage = 100 - newValue;
      const totalOtherPercentage = otherIngredients.reduce((sum, item) => sum + item.pourcentage, 0);
      
      if (totalOtherPercentage > 0) {
        otherIngredients.forEach((item, otherIndex) => {
          const actualIndex = newProportions.findIndex(p => p.ingredientId === item.ingredientId);
          newProportions[actualIndex].pourcentage = Math.round((item.pourcentage / totalOtherPercentage) * remainingPercentage);
        });
      }
      
      // Mettre à jour les grammes en fonction des nouveaux pourcentages
      const totalGrammes = newProportions.reduce((sum, item) => sum + item.pourcentage, 0);
      newProportions.forEach(item => {
        item.grammes = Math.round((item.pourcentage / 100) * totalGrammes);
      });
    } else {
      // Ajustement en grammes - indépendant pour chaque ingrédient
      const currentGrammes = newProportions[ingredientIndex].grammes || Math.round((newProportions[ingredientIndex].pourcentage / 100) * 100);
      const newGrammes = Math.max(1, currentGrammes + change); // Minimum 1g, pas de limite haute
      
      // Mettre à jour les grammes pour cet ingrédient
      newProportions[ingredientIndex].grammes = newGrammes;
      
      // S'assurer que tous les autres ingrédients ont des valeurs de grammes définies
      newProportions.forEach((item, index) => {
        if (index !== ingredientIndex && !item.grammes) {
          item.grammes = Math.round((item.pourcentage / 100) * 100);
        }
      });
      
      // Recalculer les pourcentages en fonction du nouveau total
      const totalGrammes = newProportions.reduce((sum, item) => sum + (item.grammes || 0), 0);
      
      if (totalGrammes > 0) {
        newProportions.forEach((item) => {
          const newPourcentage = Math.round((item.grammes / totalGrammes) * 100);
          item.pourcentage = isNaN(newPourcentage) ? 0 : newPourcentage;
        });
      } else {
        // Si le total est 0, répartir équitablement
        const equalPercentage = Math.round(100 / newProportions.length);
        newProportions.forEach((item) => {
          item.pourcentage = equalPercentage;
        });
      }
    }
    
    // Validation finale pour éviter les valeurs NaN
    newProportions.forEach((item) => {
      if (isNaN(item.pourcentage)) item.pourcentage = 0;
      if (isNaN(item.grammes)) item.grammes = 0;
    });
    
    setCustomProportions(newProportions);
  };

  // Fonction pour réinitialiser les proportions
  const resetProportions = useCallback(() => {
    if (comboData?.itemsList?.length) {
      if (!showPercentage) {
        // Mode grammes : réinitialiser avec 100g total selon les proportions optimales
        const proportionsFor100g = comboData.itemsList.map((item, index) => {
          const originalDosage = comboData.dosages[index];
          const pourcentage = originalDosage?.pourcentage || Math.round(100 / comboData.itemsList.length);
          return {
            ingredientId: item._id,
            name: item.name,
            image: item.image,
            pourcentage: pourcentage,
            grammes: Math.round((pourcentage / 100) * 100) // 100g total
          };
        });
        setCustomProportions(proportionsFor100g);
      } else {
        // Mode pourcentage : utiliser les proportions originales
        const originalProportions = initializeProportions(comboData.itemsList, comboData.dosages);
        setCustomProportions(originalProportions);
      }
    }
  }, [comboData, initializeProportions, showPercentage]);

  // Fonction pour récupérer les données nutritionnelles des ingrédients
  const fetchIngredientsNutrition = async (itemsList) => {
    if (!itemsList?.length) return null;
    
    setNutritionLoading(true);
    console.log('fetchIngredientsNutrition - starting for:', itemsList.map(item => item.name));
    
    try {
      const nutritionPromises = itemsList.map(async (item) => {
        try {
          console.log(`Fetching nutrition for: ${item.name} (ID: ${item._id})`);
          const response = await api.get(`/api/public/ingredient/${item._id}`);
          const ingredientData = response?.data?.data || response?.data;
          
          console.log(`Nutrition data for ${item.name}:`, ingredientData?.seo?.nutritionPer100g);
          
          return {
            ...item,
            nutritionPer100g: ingredientData?.seo?.nutritionPer100g || null
          };
        } catch (error) {
          console.error(`Error fetching nutrition for ${item.name}:`, error);
          return {
            ...item,
            nutritionPer100g: null
          };
        }
      });
      
      const enrichedItems = await Promise.all(nutritionPromises);
      console.log('fetchIngredientsNutrition - enriched items:', enrichedItems);
      
      return enrichedItems;
    } catch (error) {
      console.error('Error in fetchIngredientsNutrition:', error);
      return null;
    } finally {
      setNutritionLoading(false);
    }
  };

  // Fonction pour calculer les valeurs nutritionnelles du combo
  const calculateComboNutrition = useMemo(() => {
    console.log('calculateComboNutrition - comboData:', comboData);
    console.log('calculateComboNutrition - nutritionData:', nutritionData);
    console.log('calculateComboNutrition - customProportions:', customProportions);
    
    // Utiliser les proportions personnalisées ou les dosages par défaut
    let proportions = customProportions || 
      (comboData?.dosages || 
        comboData?.itemsList?.map(() => ({ pourcentage: 100 / (comboData?.itemsList?.length || 1) }))) || 
      [];
    
    // Valider et nettoyer les proportions pour éviter les valeurs NaN
    proportions = proportions.map(prop => ({
      ...prop,
      pourcentage: isNaN(prop.pourcentage) || prop.pourcentage === null || prop.pourcentage === undefined 
        ? 0 
        : Number(prop.pourcentage),
      grammes: isNaN(prop.grammes) || prop.grammes === null || prop.grammes === undefined 
        ? 0 
        : Number(prop.grammes)
    }));
    
    console.log('calculateComboNutrition - proportions cleaned:', proportions);
    
    if (!nutritionData?.length || !proportions?.length) {
      console.log('calculateComboNutrition - missing data:', {
        nutritionDataLength: nutritionData?.length,
        proportionsLength: proportions?.length
      });
      return null;
    }

    console.log('calculateComboNutrition - itemsList:', nutritionData.map(item => ({
      name: item.name,
      hasNutrition: !!item.nutritionPer100g,
      nutrition: item.nutritionPer100g
    })));

    const nutritionTotals = {
      calories: 0,
      protein: 0,
      carbs: 0,
      sugar: 0,
      fat: 0,
      saturatedFat: 0,
      fiber: 0,
      salt: 0,
      glycemicIndex: 0
    };

    let totalWeight = 0;
    let hasGlycemicIndex = false;
    let glycemicSum = 0;
    let glycemicWeightSum = 0;
    let hasNutritionData = false;

    // Calculer les valeurs pondérées pour chaque ingrédient
    nutritionData.forEach((ingredient, index) => {
      const proportion = proportions[index];
      if (!proportion || !ingredient.nutritionPer100g) {
        console.log(`calculateComboNutrition - skipping ingredient ${ingredient.name}:`, {
          hasProportion: !!proportion,
          hasNutrition: !!ingredient.nutritionPer100g
        });
        return;
      }

      hasNutritionData = true;
      const weight = proportion.pourcentage / 100;
      totalWeight += weight;
      
      const nutrition = ingredient.nutritionPer100g;
      console.log(`calculateComboNutrition - processing ${ingredient.name}:`, {
        weight,
        nutrition
      });

      // Additionner les macronutriments (proportionnellement)
      Object.keys(nutritionTotals).forEach(key => {
        if (key === 'glycemicIndex') {
          // L'index glycémique se calcule différemment (moyenne pondérée)
          if (nutrition[key] !== undefined && nutrition[key] !== null) {
            glycemicSum += nutrition[key] * weight;
            glycemicWeightSum += weight;
            hasGlycemicIndex = true;
          }
        } else if (nutrition[key] !== undefined && nutrition[key] !== null) {
          nutritionTotals[key] += nutrition[key] * weight;
        }
      });
    });

    if (!hasNutritionData) {
      console.log('calculateComboNutrition - no nutrition data found');
      return null;
    }

    // Normaliser si le poids total n'est pas exactement 1
    if (totalWeight > 0 && totalWeight !== 1) {
      Object.keys(nutritionTotals).forEach(key => {
        if (key !== 'glycemicIndex') {
          nutritionTotals[key] = nutritionTotals[key] / totalWeight;
        }
      });
    }

    // Calculer l'index glycémique moyen pondéré
    if (hasGlycemicIndex && glycemicWeightSum > 0) {
      nutritionTotals.glycemicIndex = glycemicSum / glycemicWeightSum;
    } else {
      nutritionTotals.glycemicIndex = null;
    }

    console.log('calculateComboNutrition - final result:', nutritionTotals);
    return nutritionTotals;
  }, [comboData, nutritionData, customProportions]);

  useEffect(() => {
    let isMounted = true;

    const fetchComboData = async () => {
      console.log('fetchComboData called with comboId:', comboId);
      
      if (!comboId) {
        console.log('No comboId provided');
        setError('ID de combo manquant');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        console.log('Making API call to:', `/api/public/combo/${comboId}`);
        const response = await api.get(`/api/public/combo/${comboId}`);
        console.log('API response:', response);
        
        if (isMounted) {
          const data = response?.data?.data || response?.data;
          console.log('Setting combo data:', data);
          console.log('itemsList details:', data.itemsList);
          console.log('proportions:', data.proportions);
          console.log('dosages:', data.dosages);
          setComboData(data);
          
          // Récupérer les données nutritionnelles des ingrédients
          if (data.itemsList?.length) {
            console.log('Starting to fetch nutrition data for ingredients...');
            fetchIngredientsNutrition(data.itemsList).then(enrichedItems => {
              if (isMounted && enrichedItems) {
                console.log('Setting nutrition data:', enrichedItems);
                setNutritionData(enrichedItems);
                
                // Initialiser les proportions personnalisées
                const initialProportions = initializeProportions(data.itemsList, data.dosages);
                setCustomProportions(initialProportions);
              }
            });
          }
        }
      } catch (err) {
        console.error('Erreur lors du chargement du combo:', err);
        if (isMounted) {
          setError('Impossible de charger les détails de cette combinaison d\'ingrédients.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchComboData();

    return () => {
      isMounted = false;
    };
  }, [comboId, initializeProportions]);

  const metaDescription = comboData?.description || 
    `Découvrez la combinaison d'ingrédients ${comboName} avec des idées de recettes et conseils pour sublimer ces saveurs dans vos plats.`;

  return (
    <>
      <Seo
        title={`YAQAM - Idées recettes avec ${comboName} : Comment maîtriser ce mélange dans vos assiettes?`}
        description={`Explorez des idées de recettes créatives et savoureuses qui mettent en valeur ${comboName.toLowerCase()}. Apprenez à sublimer ses ingrédients dans vos plats quotidiens.`}
        breadcrumb
        jsonLd={[
          // Organization schema (site owner)
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "YAQAM",
            "url": (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com',
            "logo": "https://www.yaqam.com/img/logo_color-min.png",
            "contactPoint": [{
              "@type": "ContactPoint",
              "contactType": "customer support",
              "email": "info@j7lab.com"
            }]
          },
          // BreadcrumbList for the combo page (Accueil > Idées de recettes > Combinaison)
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Accueil",
                "item": (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com'
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Idées de recettes",
                "item": ((typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com') + '/idees-de-repas'
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": comboName || 'Combinaison d\'ingrédients',
                "item": ((typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com') + window.location.pathname
              }
            ]
          }
        ]}
      />
      
      {/* Bannière avec image de fond */}
      {comboData?._id && (
        <div 
          className="relative bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${CDN}/combos/${comboData._id}.png)`,
            backgroundColor: '#f3f4f6', // Couleur de fallback
            height: '500px'
          }}
        >
          {/* Overlay gradient pour améliorer la lisibilité */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/55 to-black/80 shadow-2xl"></div>
          
          {/* Contenu de la bannière */}
          <div className="relative h-full flex items-center justify-center">
            <div className="max-w-4xl mx-auto px-4 py-12 w-full text-center">
              <div className="text-white space-y-4">
                <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide bg-white/20 backdrop-blur-sm">
                  Combinaison • Ingrédients • Recettes
                </span>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight" style={{ color: '#FFFFFF' }}>
                  Idées recettes avec {comboName} : Comment maîtriser ce mélange dans vos assiettes?
                </h1>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold leading-tight max-w-4xl mx-auto" style={{ color: '#FFFFFF' }}>
                  Explorez des idées de recettes créatives et savoureuses qui mettent en valeur {comboName.toLowerCase()}. Apprenez à sublimer ses ingrédients dans vos plats quotidiens.
                </h2>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-gray-100 px-4 py-10">
        <div className="max-w-4xl mx-auto space-y-10">

          {isLoading ? (
            <div className="flex justify-center py-16">
              <div className="h-10 w-10 rounded-full border-4" style={{ borderColor: 'rgba(230,126,34,0.2)', borderTopColor: '#E67E22' }} />
            </div>
          ) : error ? (
            <div className="rounded-2xl px-5 py-4 text-sm" style={{ color: '#92400e', backgroundColor: 'rgba(246,225,205,0.9)', border: '1px solid rgba(230,126,34,0.15)' }}>
              {error}
            </div>
          ) : (
            <>
              {/* Section À propos de cette combinaison avec image */}
              <section id="a-propos" className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                <h3 className="text-xl font-semibold mb-4" style={{ color: '#2E3500' }}>
                  À propos de la combinaison {comboName}
                </h3>
                <div className="flex flex-col lg:flex-row gap-6 items-start">
                  {comboData?._id && (
                    <div className="lg:w-1/3 flex-shrink-0">
                      <img 
                        src={`${CDN}/combos/${comboData._id}.png`}
                        alt={`Combinaison ${comboName}`}
                        className="w-full h-auto rounded-2xl object-cover"
                        style={{ maxHeight: '300px' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className={`${comboData?._id ? 'lg:w-2/3' : 'w-full'}`}>
                    <p className="text-base sm:text-lg mb-4" style={{ color: '#6c757d' }}>
                      {comboData?.description || 
                       `Cette combinaison d'ingrédients ${comboName.toLowerCase()} offre un équilibre parfait de saveurs et de textures. Découvrez comment intégrer ces ingrédients dans vos recettes pour créer des plats savoureux et nutritifs.`}
                    </p>
                  </div>
                </div>
              </section>

              {/* Table des matières */}
              <section className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                <h3 className="text-xl font-semibold mb-6" style={{ color: '#2E3500' }}>Table des matières</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                  <a href="#a-propos" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                    <span className="font-medium">1.</span>
                    <span>À propos de la combinaison {comboName}</span>
                  </a>
                  <a href="#ingredients" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                    <span className="font-medium">2.</span>
                    <span>Les ingrédients de cette combinaison : {comboName}</span>
                  </a>
                  <a href="#recettes" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                    <span className="font-medium">3.</span>
                    <span>Les meilleures idées de recettes avec {comboName.toLowerCase()} ({comboData.recipeList.length})</span>
                  </a>
                  <a href="#savor-balance" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                    <span className="font-medium">4.</span>
                    <span>Conseil de YAQAM pour un équilibre de saveurs parfait entre {comboName.toLowerCase()} dans vos repas</span>
                  </a>
                  <a href="#equilibre-nutritionnel" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                    <span className="font-medium">5.</span>
                    <span>Calculateur de l'équilibre nutritionnel du mélange entre {comboName.toLowerCase()}</span>
                  </a>
                  <a href="#conseils" className="flex items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors" style={{ color: '#87A96B' }}>
                    <span className="font-medium">6.</span>
                    <span>Nos conseils pour cuisiner le combo {comboName.toLowerCase()}</span>
                  </a>
                </div>
              </section>

              {/* Section Ingrédients */}
              {comboData?.itemsList?.length > 0 && (
                <section id="ingredients" className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                  <h3 className="text-lg sm:text-xl font-semibold mb-4" style={{ color: '#2E3500' }}>
                    Les ingrédients de cette combinaison : {comboName}
                  </h3>
                  <p className="text-sm sm:text-base mb-6" style={{ color: '#6c757d' }}>
                    Cette combinaison réunit <strong>{comboData.nbIngredients} ingrédient{comboData.nbIngredients > 1 ? 's' : ''}</strong>.
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {comboData.itemsList.map((ingredient, index) => (
                      <Link
                        key={ingredient._id || index}
                        to={`/idees-de-repas/idees-recettes/${ingredient.slug}/${ingredient._id}`}
                        className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden transition-transform hover:-translate-y-1 hover:shadow-lg block"
                      >
                        <div className="relative h-32">
                          <img 
                            src={`${CDN}/ingredients/${ingredient._id}.png`}
                            alt={ingredient.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                        <div className="p-4">
                          <p className="text-base font-semibold" style={{ color: '#2E3500' }}>
                            {ingredient.name}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {/* Section Recettes */}
              {comboData?.recipeList?.length > 0 ? (
                <section id="recettes" className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                  <h3 className="text-lg sm:text-xl font-semibold mb-4" style={{ color: '#2E3500' }}>
                    Les meilleures idées de recettes avec {comboName.toLowerCase()} ({comboData.recipeList.length})
                  </h3>
                  <p className="text-sm sm:text-base mb-6" style={{ color: '#6c757d' }}>
                    Découvrez <strong>{comboData.recipeList.length} recette{comboData.recipeList.length > 1 ? 's' : ''}</strong> générées par YAQAM, spécialement conçue{comboData.recipeList.length > 1 ? 's' : ''} pour mettre en valeur cette combinaison d'ingrédients. Vous aussi, créez vos propres idées de recettes mélangeant {comboName.toLowerCase()} ! <a href="/register">Inscrivez-vous gratuitement sur YAQAM pour commencer.</a>
                  </p>
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {comboData.recipeList.map((recipe, index) => (
                      <article
                        key={recipe._id || index}
                        className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col h-full transition-transform hover:-translate-y-1 hover:shadow-lg"
                      >
                        <Link to={`/idees-de-repas/idees-recette/${recipe.url}/${recipe._id}`} className="block relative h-48">
                          <img 
                            src={`${CDN}/recipe/${recipe._id}-0.jpg`}
                            alt={recipe.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = '/img/placeholder-recipe.jpg';
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity"></div>
                        </Link>
                        <div className="p-6 flex flex-col flex-1">
                          <Link 
                            to={`/idees-de-repas/idees-recette/${recipe.url}/${recipe._id}`} 
                            className="text-sm font-semibold mb-2 hover:underline text-center" 
                            style={{ color: '#2E3500' }}
                          >
                            {recipe.name || `Recette ${index + 1}`}
                          </Link>
                          <div className="mt-auto">
                            <Link
                              to={`/idees-de-repas/idees-recette/${recipe.url}/${recipe._id}`}
                              className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium rounded-full text-white"
                              style={{ backgroundColor: '#87A96B' }}
                            >
                              Voir la recette
                            </Link>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ) : (
                <section id="recettes" className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                  <h3 className="text-lg sm:text-xl font-semibold mb-2" style={{ color: '#2E3500' }}>
                    Recettes avec {comboName.toLowerCase()}
                  </h3>
                  <p className="text-sm sm:text-base" style={{ color: '#6c757d' }}>
                    Les recettes pour cette combinaison d'ingrédients sont en cours de préparation. Revenez bientôt pour découvrir des idées créatives !
                  </p>
                </section>
              )}

              {/* Section Conseil de YAQAM pour bon équilibre */}
              <section id="savor-balance" className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                <h3 className="text-lg sm:text-xl font-semibold mb-4" style={{ color: '#2C3E50' }}>
                  Conseil de YAQAM pour un équilibre de saveurs parfait entre {comboName.toLowerCase()} dans vos repas
                </h3>
                
                {comboData?.optimalDosage?.length > 0 ? (
                  <div className="space-y-4">
                      <p  className="text-sm sm:text-base mb-6" style={{ color: '#6c757d' }}>
                        Voici l'équilibre optimal selon YAQAM pour <strong>vos idées de recettes avec {comboName.toLowerCase()}</strong> :
                      </p>
                    
                    {/* Graphique de répartition */}
                    <div className="space-y-4">
                      {/* Barre unique avec tous les ingrédients */}
                      <div className="relative">
                        <div className="h-8 bg-gray-200 rounded-full overflow-hidden flex">
                          {comboData.optimalDosage.map((dosage, index) => {
                            const ingredient = comboData.itemsList?.find(item => item._id === dosage.normalized_ingredient_id);
                            if (!ingredient) return null;
                            
                            return (
                              <div
                                key={dosage.normalized_ingredient_id || index}
                                className="h-full flex items-center justify-center transition-all duration-300"
                                style={{ 
                                  width: `${dosage.pourcentage}%`,
                                  backgroundColor: index === 0 ? '#87A96B' : index === 1 ? '#E67E22' : index === 2 ? '#2C3E50' : '#9B59B6'
                                }}
                              >
                                {dosage.pourcentage >= 15 && (
                                  <span className="text-xs font-semibold text-white">
                                    {dosage.pourcentage}%
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Légende des ingrédients */}
                      <div className="flex flex-wrap gap-6">
                        {comboData.optimalDosage.map((dosage, index) => {
                          const ingredient = comboData.itemsList?.find(item => item._id === dosage.normalized_ingredient_id);
                          if (!ingredient) return null;
                          
                          return (
                            <div key={dosage.normalized_ingredient_id || index} className="flex items-center gap-4">
                              <img 
                                src={`${CDN}/ingredients/${ingredient._id}.png`}
                                alt={ingredient.name}
                                className="w-12 h-12 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                              <div 
                                className="w-6 h-6 rounded-full"
                                style={{ 
                                  backgroundColor: index === 0 ? '#87A96B' : index === 1 ? '#E67E22' : index === 2 ? '#2C3E50' : '#9B59B6'
                                }}
                              />
                              <span className="text-lg font-semibold" style={{ color: '#2C3E50' }}>
                                {ingredient.name} ({dosage.pourcentage}%)
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600">
                        💡 Ces proportions sont indicatives et peuvent être ajustées selon vos goûts personnels.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#f3f7f4', border: '1px solid #e6f0e6' }}>
                    <p className="text-sm sm:text-base" style={{ color: '#2E3500' }}>
                      {comboData?.balanceTip || 
                       `Pour obtenir le meilleur équilibre avec ${comboName.toLowerCase()}, YAQAM recommande de respecter les proportions naturelles de chaque ingrédient. Cette combinaison a été soigneusement étudiée pour offrir une harmonie parfaite des saveurs et des textures. Ajustez les quantités selon vos préférences tout en gardant l'esprit de cette association.`}
                    </p>
                  </div>
                )}
              </section>

              {/* Section Équilibre nutritionnel */}
              <section id="equilibre-nutritionnel" className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                <h3 className="text-lg sm:text-xl font-semibold mb-4" style={{ color: '#2C3E50' }}>
                  Calculateur de l'équilibre nutritionnel du mélange entre {comboName.toLowerCase()}
                </h3>

                {nutritionLoading ? (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#f3f7f4', border: '1px solid #e6f0e6' }}>
                    <h4 className="font-semibold mb-2" style={{ color: '#2E3500' }}>⏳ Chargement des données nutritionnelles...</h4>
                    <p className="text-sm" style={{ color: '#2E3500' }}>
                      Récupération des informations nutritionnelles de chaque ingrédient en cours...
                    </p>
                  </div>
                ) : calculateComboNutrition ? (
                  <div className="space-y-4">
                    {/* Ajustement interactif des proportions */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-gray-800">🥄 Composition du mélange</h4>
                        <button
                          onClick={resetProportions}
                          className="px-3 py-1 rounded-lg text-sm font-medium"
                          style={{ backgroundColor: '#FFEDE3', color: '#E67E22', border: '1px solid rgba(230,126,34,0.15)' }}
                        >
                          Réinitialiser
                        </button>
                      </div>

                      {/* Interface d'ajustement directe */}
                      <div className="space-y-4">
                        <div className="flex justify-center mb-4">
                          <div className="flex bg-gray-200 rounded-lg p-1">
                            <button
                              onClick={() => setShowPercentage(false)}
                              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 ${
                                !showPercentage ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                              }`}
                            >
                              Grammes (g)
                            </button>
                            <button
                              onClick={() => setShowPercentage(true)}
                              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 ${
                                showPercentage ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                              }`}
                            >
                              Pourcentages (%)
                            </button>
                          </div>
                        </div>

                        <div className="text-center mb-4 text-sm text-gray-600">
                          {showPercentage 
                            ? 'Ajustez les proportions en % (total = 100%)' 
                            : 'Ajustez les quantités en grammes (indépendamment pour chaque ingrédient)'
                          }
                        </div>

                        {!showPercentage && (
                          <div className="text-center mb-4 p-3 rounded-lg" style={{ backgroundColor: '#f3f7f4', border: '1px solid #e6f0e6' }}>
                            <span className="text-sm font-semibold" style={{ color: '#2E3500' }}>
                              Poids Total : {
                                (customProportions || initializeProportions(comboData?.itemsList, comboData?.dosages))
                                  ?.reduce((total, ingredient) => total + (ingredient.grammes || 0), 0) || 0
                              }g
                            </span>
                          </div>
                        )}

                        {(customProportions || initializeProportions(comboData?.itemsList, comboData?.dosages))?.map((ingredient, index) => (
                          <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <img 
                                  src={`${CDN}/ingredients/${ingredient.ingredientId}.png`}
                                  alt={ingredient.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                  onError={(e) => { e.target.src = '/img/default.png' }}
                                />
                                <span className="font-medium text-gray-900">{ingredient.name}</span>
                              </div>
                              
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => adjustProportion(index, -5, showPercentage)}
                                  className="w-8 h-8 rounded-full flex items-center justify-center font-bold"
                                  style={{ backgroundColor: '#FFEDE3', color: '#E67E22', border: '1px solid rgba(230,126,34,0.12)' }}
                                >
                                  −
                                </button>
                                
                                <div className="min-w-[80px] text-center">
                                  <span className="text-lg font-semibold text-gray-900">
                                    {showPercentage 
                                      ? `${ingredient.pourcentage || 0}%`
                                      : `${ingredient.grammes || Math.round((ingredient.pourcentage || 0) / 100 * 100) || 0}g`
                                    }
                                  </span>
                                </div>
                                
                                <button
                                  onClick={() => adjustProportion(index, 5, showPercentage)}
                                  className="w-8 h-8 rounded-full flex items-center justify-center font-bold"
                                  style={{ backgroundColor: '#F2FBF5', color: '#87A96B', border: '1px solid rgba(135,169,107,0.12)' }}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        <div className="text-center text-sm" style={{ color: '#2E3500', marginTop: '1rem' }}>
                          💡 Les valeurs nutritionnelles sont recalculées automatiquement
                        </div>
                      </div>
                    </div>

                    {/* Informations générales */}
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#f3f7f4', border: '1px solid #e6f0e6' }}>
                      <h4 className="font-semibold mb-2" style={{ color: '#2E3500' }}>📊 Valeurs nutritionnelles du mélange</h4>
                      <p className="text-sm" style={{ color: '#2E3500' }}>
                        Pour {showPercentage ? 'une portion équilibrée' : '100g'} de {comboName.toLowerCase()}, 
                        voici la composition nutritionnelle calculée selon les proportions de chaque ingrédient.
                      </p>
                    </div>

                    {/* Tableau nutritionnel */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm sm:text-base">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2E3500' }}>Élément nutritif</th>
                            <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2E3500' }}>Quantité</th>
                            <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2E3500' }}>Proportion</th>
                            <th className="text-left py-2 px-3 font-semibold" style={{ color: '#2E3500' }}>Niveau</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { name: 'Calories', value: calculateComboNutrition.calories, unit: 'kcal', key: 'calories' },
                            { name: 'Protéines', value: calculateComboNutrition.protein, unit: 'g', key: 'protein' },
                            { name: 'Glucides', value: calculateComboNutrition.carbs, unit: 'g', key: 'carbs' },
                            { name: 'Sucres', value: calculateComboNutrition.sugar, unit: 'g', key: 'sugar' },
                            { name: 'Lipides', value: calculateComboNutrition.fat, unit: 'g', key: 'fat' },
                            { name: 'Acides gras saturés', value: calculateComboNutrition.saturatedFat, unit: 'g', key: 'saturatedFat' },
                            { name: 'Fibres', value: calculateComboNutrition.fiber, unit: 'g', key: 'fiber' },
                            { name: 'Sel', value: calculateComboNutrition.salt, unit: 'g', key: 'salt' },
                            { name: 'Index glycémique', value: calculateComboNutrition.glycemicIndex, unit: '', key: 'glycemicIndex' }
                          ].filter(nutrient => nutrient.value !== null && nutrient.value !== undefined).map((nutrient, index) => {
                            const level = getNutritionLevel(nutrient.key, nutrient.value);
                            const levelColor = getNutritionLevelColor(level);
                            
                            // Calculer les pourcentages basés sur les besoins quotidiens approximatifs
                            let percentage = null;
                            const dailyNeeds = {
                              calories: 2000,
                              protein: 50,
                              carbs: 250,
                              sugar: 25,
                              fat: 70,
                              saturatedFat: 20,
                              fiber: 25,
                              salt: 6
                            };
                            if (dailyNeeds[nutrient.key]) {
                              percentage = Math.round((nutrient.value / dailyNeeds[nutrient.key]) * 100);
                            }
                            
                            return (
                              <tr key={index} className="border-b border-gray-100 last:border-b-0">
                                <td className="py-3 px-3 font-medium" style={{ color: '#2E3500' }}>
                                  {nutrient.name}
                                </td>
                                <td className="py-3 px-3" style={{ color: '#6c757d' }}>
                                  {formatNutritionValue(nutrient.value, nutrient.unit)}
                                </td>
                                <td className="py-3 px-3" style={{ color: '#6c757d' }}>
                                  {percentage !== null ? `${percentage}%` : '-'}
                                </td>
                                <td className="py-3 px-3">
                                  <span 
                                    className="px-2 py-1 text-xs font-medium rounded-full capitalize"
                                    style={{ 
                                      backgroundColor: level !== '-' ? `${levelColor}20` : '#f3f4f6',
                                      color: levelColor
                                    }}
                                  >
                                    {level !== '-' ? level : 'N/A'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#fff6f1', border: '1px solid #fdebd6' }}>
                      <h4 className="font-semibold mb-2" style={{ color: '#E67E22' }}>⚠️ Données nutritionnelles indisponibles</h4>
                      <p className="text-sm" style={{ color: '#2E3500' }}>
                        Les informations nutritionnelles détaillées ne sont pas disponibles pour cette combinaison. 
                        Nous travaillons à enrichir notre base de données nutritionnelles.
                      </p>
                    </div>
                    
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#f2faf4', border: '1px solid #e6f4e8' }}>
                      <h4 className="font-semibold mb-2" style={{ color: '#87A96B' }}>🥗 Apports nutritionnels généraux</h4>
                      <p className="text-sm" style={{ color: '#2E3500' }}>
                        Cette combinaison d'ingrédients offre un équilibre nutritionnel intéressant. Chaque élément apporte ses propres bénéfices : vitamines, minéraux, fibres et antioxydants se complètent harmonieusement.
                      </p>
                    </div>
                    
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#f3f7f4', border: '1px solid #e6f0e6' }}>
                      <h4 className="font-semibold mb-2" style={{ color: '#2E3500' }}>⚖️ Équilibre des macronutriments</h4>
                      <p className="text-sm" style={{ color: '#2E3500' }}>
                        {comboName} présente une répartition équilibrée entre glucides, protéines et lipides, favorisant une sensation de satiété durable et un apport énergétique stable.
                      </p>
                    </div>
                  </div>
                )}
              </section>

              {/* Section Conseils de préparation */}
              <section id="conseils" className="rounded-3xl border border-gray-200 bg-white shadow-sm p-6">
                <h3 className="text-lg sm:text-xl font-semibold mb-4" style={{ color: '#2E3500' }}>
                  Nos conseils pour cuisiner le combo {comboName.toLowerCase()}
                </h3>
                <div className="space-y-4">
                  {comboData?.cookingTips?.length > 0 ? (
                    comboData.cookingTips.map((tip, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-700">
                          {tip}
                        </p>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#f2faf4', border: '1px solid #e6f4e8' }}>
                        <h4 className="font-semibold mb-2" style={{ color: '#87A96B' }}>🌿 Fraîcheur optimale</h4>
                        <p className="text-sm" style={{ color: '#2E3500' }}>
                          Pour tirer le meilleur parti de cette combinaison, privilégiez des ingrédients frais et de saison. 
                          La qualité des produits est essentielle pour révéler toutes les saveurs.
                        </p>
                      </div>
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#f3f7f4', border: '1px solid #e6f0e6' }}>
                        <h4 className="font-semibold mb-2" style={{ color: '#2E3500' }}>⚖️ Équilibre des saveurs</h4>
                        <p className="text-sm" style={{ color: '#2E3500' }}>
                          Dosez harmonieusement chaque ingrédient selon votre goût. Cette combinaison offre 
                          un équilibre naturel, mais n'hésitez pas à ajuster selon vos préférences.
                        </p>
                      </div>
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#fff6f1', border: '1px solid rgba(230,126,34,0.15)' }}>
                        <h4 className="font-semibold mb-2" style={{ color: '#E67E22' }}>🍳 Techniques de cuisson</h4>
                        <p className="text-sm" style={{ color: '#2E3500' }}>
                          Respectez les temps de cuisson de chaque ingrédient pour préserver leurs textures 
                          et leurs propriétés nutritionnelles. Ajoutez les plus fragiles en fin de cuisson.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </section>
            </>
          )}

          {/* Call-to-action */}
          <aside className="bg-white rounded-3xl border border-gray-200 shadow-md p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="space-y-2">
              <h3 className="text-xl font-semibold" style={{ color: '#2E3500' }}>
                Explorez d'autres combinaisons d'ingrédients
              </h3>
              <p className="text-sm sm:text-base" style={{ color: '#6c757d' }}>
                YAQAM vous propose des milliers de combinaisons d'ingrédients pour varier vos menus.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Link
                to="/idees-de-repas/idees-recettes"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-semibold"
                style={{ border: '1px solid #87A96B', color: '#87A96B' }}
              >
                Découvrir les ingrédients
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-semibold text-white"
                style={{ backgroundColor: '#E67E22' }}
              >
                Créer mon compte YAQAM
              </Link>
            </div>
          </aside>

          {/* Section date de mise à jour */}
          <div className="text-center py-4">
            <p className="text-xs text-gray-500">
              Dernière mise à jour : {comboData?.updatedAt ? 
                new Date(comboData.updatedAt).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 
                new Date().toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })
              }
            </p>
          </div>
        </div>
      </div>
    </>
  );
}